import React from "react";
import { useBluelagoon } from "@bluelagoon";
import Button from "../../components/Button";
import Input from "../../components/Input";
import InternalLink from "../../components/InternalLink";
import { useAuth } from "../../context/AuthContext";
import Message from "../../components/Message";
import { useTranslation } from "react-i18next";
import { track, trackSignup } from "@crm/analytics";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@shadcn-in-nx/ui/tabs";

function AuthModal({ open, onOpenChange, onAuthSuccess }) {
    const { t, i18n } = useTranslation();

    // Restore OTP flow state from sessionStorage on mount
    const savedOtpState = React.useMemo(() => {
        try {
            const raw = sessionStorage.getItem("auth_modal_otp_state");
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const elapsed = Math.floor((Date.now() - parsed.codeRequestedAt) / 1000);
            if (elapsed > 300) {
                sessionStorage.removeItem("auth_modal_otp_state");
                return null;
            }
            return { ...parsed, elapsed };
        } catch {
            return null;
        }
    }, []);

    const [email, setEmail] = React.useState(savedOtpState?.email || "");
    const [fullName, setFullName] = React.useState("");
    const [acceptedTerms, setAcceptedTerms] = React.useState(false);
    const [isTokenStep, setIsTokenStep] = React.useState(!!savedOtpState);
    const [successMessage, setSuccessMessage] = React.useState(
        savedOtpState ? savedOtpState.successMessage : null
    );
    const [error, setError] = React.useState(null);
    const [isLoadingGoogleUrl, setIsLoadingGoogleUrl] = React.useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState(
        savedOtpState?.activeTab || "login"
    );
    const [resendCountdown, setResendCountdown] = React.useState(() => {
        if (!savedOtpState) return 0;
        const remaining = savedOtpState.countdownDuration - savedOtpState.elapsed;
        return remaining > 0 ? remaining : 0;
    });
    const [resendAttempts, setResendAttempts] = React.useState(
        savedOtpState?.resendAttempts || 0
    );
    const [isResending, setIsResending] = React.useState(false);
    const { getUser, setToken, isLoadingUser } = useAuth();

    // Login mutation
    const {
        call: login,
        error: loginError,
        isLoading: isLoginLoading,
    } = useBluelagoon({
        method: "login",
    });

    // Signup mutation
    const {
        call: signup,
        error: signupError,
        isLoading: isSignupLoading,
    } = useBluelagoon({
        method: "signup",
    });

    // Request code mutation
    const {
        call: requestCode,
        error: requestCodeError,
        isLoading: requestCodeIsLoading,
    } = useBluelagoon({
        method: "userRequestCode",
    });

    // Google auth URL mutation
    const {
        call: getGoogleUrl,
        error: googleUrlError,
        isLoading: googleUrlLoading,
    } = useBluelagoon({
        method: "getGoogleAuthUrlForLogin",
    });

    // Add new state for Microsoft auth loading
    const [isMicrosoftLoading, setIsMicrosoftLoading] = React.useState(false);

    // Microsoft auth URL mutation
    const {
        call: getMicrosoftUrl,
        error: microsoftUrlError,
        isLoading: microsoftUrlLoading,
    } = useBluelagoon({
        method: "getMicrosoftAuthUrlForLogin",
    });

    // SSO check mutation
    const { call: checkSSO } = useBluelagoon({ method: "checkSSOByEmail" });

    // SSO login mutation
    const { call: ssoLogin } = useBluelagoon({ method: "ssoLoginUser" });

    const [ssoAvailable, setSsoAvailable] = React.useState(false);
    const [isSSOLoading, setIsSSOLoading] = React.useState(false);

    const handleGetUser = React.useCallback(async () => {
        try {
            const user = await getUser();
            if (onAuthSuccess) {
                onAuthSuccess(user);
            }
        } catch (error) {
            console.error("Failed to get user:", error);
        }
    }, [getUser, onAuthSuccess]);

    // Handle token submission for login
    const handleLoginTokenSubmit = async (event) => {
        event.preventDefault();
        if (isLoginLoading) return;
        const formData = new FormData(event.target);
        const credentials = Object.fromEntries(formData.entries());

        track("login_token_submitted", {
            email: credentials.email,
            method: "email_token",
        });

        try {
            const response = await login(credentials);
            setToken(response?.accessToken);
            sessionStorage.removeItem("auth_modal_otp_state");
            sessionStorage.removeItem("auth_modal_open");
            track("login_success", {
                email: credentials.email,
                method: "email_token",
            });
            await handleGetUser();
        } catch (error) {
            console.log("error", error);
            track("login_failed", {
                email: credentials.email,
                method: "email_token",
                error: error?.message || t("auth.modal.unknownError"),
            });
        }
    };

    // Handle email submission for login
    const handleLoginEmailSubmit = async (event) => {
        event.preventDefault();
        if (requestCodeIsLoading) return;
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData.entries());

        track("login_attempt_started", {
            email: data.email,
            method: "email_token",
        });

        try {
            const response = await requestCode(data);
            const message = response?.message || t("auth.modal.codeSentMessage");
            setIsTokenStep(true);
            setError(null);
            setSuccessMessage(message);
            // Start countdown for first resend (30 seconds)
            setResendCountdown(30);
            setResendAttempts(0);

            sessionStorage.setItem("auth_modal_otp_state", JSON.stringify({
                email: data.email,
                codeRequestedAt: Date.now(),
                countdownDuration: 30,
                resendAttempts: 0,
                successMessage: message,
                activeTab: "login",
            }));

            track("login_code_requested", {
                email: data.email,
                method: "email_token",
            });
        } catch (error) {
            setError(error);
            track("login_code_request_failed", {
                email: data.email,
                method: "email_token",
                error: error?.message || t("auth.modal.unknownError"),
            });
        }
    };

    // Handle signup submission
    const handleSignupSubmit = async (event) => {
        event.preventDefault();
        if (isSignupLoading) return;

        if (!acceptedTerms) {
            track("login_failed", {
                method: "email_signup",
                email: email,
                error: t("auth.modal.termsNotAccepted"),
            });
            return;
        }

        const formData = new FormData(event.target);
        const formJSON = Object.fromEntries(formData.entries());

        // Split full name into first and last name
        const fullName = formJSON.fullName || "";
        const nameParts = fullName.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName =
            nameParts.length > 1
                ? nameParts.slice(1).join(" ")
                : t("auth.modal.noLastName");

        const finalFormData = {
            ...formJSON,
            firstName,
            lastName,
            language: i18n.language,
        };

        track("login_attempt_started", {
            method: "email_signup",
            email: finalFormData.email,
            firstName: finalFormData.firstName,
            lastName: finalFormData.lastName,
        });

        try {
            const response = await signup(finalFormData);
            const message = response?.message || t("auth.modal.verificationCodeSentMessage");
            localStorage.setItem("hasCompletedTutorial", "false");
            setIsTokenStep(true);
            setSuccessMessage(message);
            // Start countdown for first resend (30 seconds)
            setResendCountdown(30);
            setResendAttempts(0);

            sessionStorage.setItem("auth_modal_otp_state", JSON.stringify({
                email: finalFormData.email,
                codeRequestedAt: Date.now(),
                countdownDuration: 30,
                resendAttempts: 0,
                successMessage: message,
                activeTab: "signup",
            }));

            trackSignup(finalFormData.email);
            track("login_code_requested", {
                method: "email_signup",
                email: finalFormData.email,
            });
        } catch (error) {
            track("login_code_request_failed", {
                method: "email_signup",
                email: finalFormData.email,
                error:
                    error?.message ||
                    error?.error ||
                    t("auth.modal.unknownError"),
            });
        }
    };

    // Handle signup verification
    const handleSignupVerification = async (event) => {
        event.preventDefault();
        if (isLoginLoading) return;
        const formData = new FormData(event.target);
        const formJSON = Object.fromEntries(formData.entries());

        track("login_token_submitted", {
            method: "email_signup",
            email: formJSON.email,
        });

        try {
            const response = await login({
                ...formJSON,
                language: i18n.language,
            });
            setToken(response?.accessToken);
            sessionStorage.removeItem("auth_modal_otp_state");
            sessionStorage.removeItem("auth_modal_open");
            if (!response?.user?.hasCompletedTutorial) {
                localStorage.setItem("hasCompletedTutorial", "false");
            }
            track("login_success", {
                method: "email_signup",
                email: formJSON.email,
            });
            await handleGetUser();
        } catch (error) {
            track("login_failed", {
                method: "email_signup",
                email: formJSON.email,
                error:
                    error?.message ||
                    error?.error ||
                    t("auth.modal.unknownError"),
            });
        }
    };

    // Handle Google sign in
    const handleGoogleSignIn = async () => {
        setIsGoogleLoading(true);
        track("login_attempt_started", {
            method: "google",
        });

        try {
            const currentUrl = window.location.href;
            const response = await getGoogleUrl({
                isAuth: true,
                currentUrl: currentUrl,
                language: i18n.language,
            });
            if (response.status === 200) {
                track("google_auth_url_generated", {
                    method: "google",
                });
                window.location.href = response.data.url;
            } else {
                setError(response.data.error);
                track("google_auth_url_failed", {
                    method: "google",
                    error: response.data.error,
                });
            }
        } catch (error) {
            setError(error);
            track("login_failed", {
                method: "google",
                error: error?.message || t("auth.modal.unknownError"),
            });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    // Handle Google sign up
    const handleGoogleSignUp = async () => {
        setIsGoogleLoading(true);
        track("login_attempt_started", {
            method: "google_signup",
        });

        try {
            const currentUrl = window.location.href;
            const response = await getGoogleUrl({
                isAuth: true,
                isShopifySignup: false,
                currentUrl: currentUrl,
                language: i18n.language,
            });
            if (response.status === 200) {
                localStorage.setItem("hasCompletedTutorial", "false");
                track("google_auth_url_generated", {
                    method: "google_signup",
                });
                window.location.href = response.data.url;
            } else {
                track("google_auth_url_failed", {
                    method: "google_signup",
                    error: response.data.error,
                });
            }
        } catch (error) {
            track("login_failed", {
                method: "google_signup",
                error: error?.message || t("auth.modal.unknownError"),
            });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    // Handle Microsoft sign in
    const handleMicrosoftSignIn = async () => {
        setIsMicrosoftLoading(true);
        track("login_attempt_started", {
            method: "microsoft",
        });

        try {
            const currentUrl = window.location.href;
            const response = await getMicrosoftUrl({
                isAuth: true,
                currentUrl: currentUrl,
                language: i18n.language,
            });
            if (response.status === 200) {
                track("microsoft_auth_url_generated", {
                    method: "microsoft",
                });
                window.location.href = response.data.url;
            } else {
                setError(response.data.error);
                track("microsoft_auth_url_failed", {
                    method: "microsoft",
                    error: response.data.error,
                });
            }
        } catch (error) {
            setError(error);
            track("login_failed", {
                method: "microsoft",
                error: error?.message || t("auth.modal.unknownError"),
            });
        } finally {
            setIsMicrosoftLoading(false);
        }
    };

    // Handle Microsoft sign up
    const handleMicrosoftSignUp = async () => {
        setIsMicrosoftLoading(true);
        track("login_attempt_started", {
            method: "microsoft_signup",
        });

        try {
            const currentUrl = window.location.href;
            const response = await getMicrosoftUrl({
                isAuth: true,
                isShopifySignup: false,
                currentUrl: currentUrl,
                language: i18n.language,
            });
            if (response.status === 200) {
                localStorage.setItem("hasCompletedTutorial", "false");
                track("microsoft_auth_url_generated", {
                    method: "microsoft_signup",
                });
                window.location.href = response.data.url;
            } else {
                track("microsoft_auth_url_failed", {
                    method: "microsoft_signup",
                    error: response.data.error,
                });
            }
        } catch (error) {
            track("login_failed", {
                method: "microsoft_signup",
                error: error?.message || t("auth.modal.unknownError"),
            });
        } finally {
            setIsMicrosoftLoading(false);
        }
    };

    // Check SSO availability when email changes
    React.useEffect(() => {
        if (!email || !email.includes("@") || isTokenStep || activeTab !== "login") {
            setSsoAvailable(false);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const res = await checkSSO({ email: email.trim() });
                setSsoAvailable(!!res?.data?.ssoEnabled);
            } catch {
                setSsoAvailable(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [email, isTokenStep, activeTab]);

    // Handle SSO login
    const handleSSOLogin = async () => {
        if (!ssoAvailable || !email) return;
        setIsSSOLoading(true);
        track("login_attempt_started", { method: "sso", email });
        try {
            const res = await ssoLogin({ email: email.trim() });
            if (res?.data?.authUrl) {
                track("sso_auth_url_generated", { method: "sso" });
                sessionStorage.removeItem("auth_modal_open");
                sessionStorage.removeItem("auth_modal_otp_state");
                window.location.href = res.data.authUrl;
                return;
            } else {
                setError(res?.data?.error || res?.error || t("auth.modal.ssoLoginFailed"));
                track("login_failed", { method: "sso", error: res?.data?.error });
            }
        } catch (err) {
            setError(err?.message || t("auth.modal.ssoLoginFailed"));
            track("login_failed", { method: "sso", error: err?.message });
        }
        setIsSSOLoading(false);
    };

    // Countdown timer effect for resend
    React.useEffect(() => {
        if (resendCountdown > 0) {
            const timer = setTimeout(() => {
                setResendCountdown(resendCountdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCountdown]);

    // Handle resend code
    const handleResendCode = async () => {
        if (resendCountdown > 0 || isResending) return;

        setIsResending(true);
        const method = activeTab === "login" ? "email_token" : "email_signup";
        track("login_code_resend_requested", {
            email: email,
            method: method,
            attempt: resendAttempts + 1,
        });

        try {
            const response = await requestCode({ email });
            const message = response?.message || t("auth.modal.codeSentMessage");
            setSuccessMessage(message);
            setResendAttempts((prev) => prev + 1);
            // After first resend, wait 2 minutes (120 seconds)
            setResendCountdown(120);

            sessionStorage.setItem("auth_modal_otp_state", JSON.stringify({
                email,
                codeRequestedAt: Date.now(),
                countdownDuration: 120,
                resendAttempts: resendAttempts + 1,
                successMessage: message,
                activeTab,
            }));

            track("login_code_resend_success", {
                email: email,
                method: method,
                attempt: resendAttempts + 1,
            });
        } catch (error) {
            setError(error);
            track("login_code_resend_failed", {
                email: email,
                method: method,
                attempt: resendAttempts + 1,
                error: error?.message || t("auth.modal.unknownError"),
            });
        } finally {
            setIsResending(false);
        }
    };

    // Reset form when modal closes (but not on mount if restoring state)
    const didRestoreRef = React.useRef(!!savedOtpState);
    React.useEffect(() => {
        if (!open) {
            // Skip reset on initial mount if we just restored state and modal is about to open
            if (didRestoreRef.current) {
                didRestoreRef.current = false;
                return;
            }
            setEmail("");
            setFullName("");
            setAcceptedTerms(false);
            setIsTokenStep(false);
            setSuccessMessage(null);
            setError(null);
            setActiveTab("login");
            setResendCountdown(0);
            setResendAttempts(0);
            sessionStorage.removeItem("auth_modal_otp_state");
        }
    }, [open]);

    // Track modal open
    React.useEffect(() => {
        if (open) {
            track("auth_modal_opened", {
                source: "chat_input",
            });
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[100vh] overflow-y-auto  sm:mx-auto">
                <DialogHeader>
                    <DialogTitle className="text-center text-xl font-bold">
                        {t("auth.login.title")}
                    </DialogTitle>
                </DialogHeader>

                <div className="mt-4">
                    {/* Google Sign In/Up Buttons */}
                    {!isTokenStep ? (
                        <>
                            <div className="space-y-3 mb-6">
                                <button
                                    className="gsi-material-button w-full"
                                    onClick={
                                        activeTab === "login"
                                            ? handleGoogleSignIn
                                            : handleGoogleSignUp
                                    }
                                    disabled={isGoogleLoading}
                                >
                                    <div className="gsi-material-button-state" />
                                    <div className="gsi-material-button-content-wrapper">
                                        <div className="gsi-material-button-icon">
                                            {isGoogleLoading ? (
                                                <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full" />
                                            ) : (
                                                <svg
                                                    version="1.1"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 48 48"
                                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                                    style={{ display: "block" }}
                                                >
                                                    <path
                                                        fill="#EA4335"
                                                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                                                    />
                                                    <path
                                                        fill="#4285F4"
                                                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                                                    />
                                                    <path
                                                        fill="#FBBC05"
                                                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                                                    />
                                                    <path
                                                        fill="#34A853"
                                                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                                                    />
                                                    <path
                                                        fill="none"
                                                        d="M0 0h48v48H0z"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <span className="gsi-material-button-contents">
                                            {isGoogleLoading
                                                ? t("auth.modal.loading")
                                                : activeTab === "login"
                                                ? t("auth.modal.googleSignIn")
                                                : t("auth.modal.googleSignUp")}
                                        </span>
                                    </div>
                                </button>

                                {/* Microsoft Sign In/Up Button */}
                                <button
                                    className="gsi-material-button w-full"
                                    onClick={
                                        activeTab === "login"
                                            ? handleMicrosoftSignIn
                                            : handleMicrosoftSignUp
                                    }
                                    disabled={isMicrosoftLoading}
                                >
                                    <div className="gsi-material-button-state" />
                                    <div className="gsi-material-button-content-wrapper">
                                        <div className="gsi-material-button-icon">
                                            {isMicrosoftLoading ? (
                                                <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-transparent rounded-full" />
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width={21}
                                                    height={21}
                                                    viewBox="0 0 21 21"
                                                >
                                                    <title>
                                                        MS-SymbolLockup
                                                    </title>
                                                    <rect
                                                        x={1}
                                                        y={1}
                                                        width={9}
                                                        height={9}
                                                        fill="#f25022"
                                                    />
                                                    <rect
                                                        x={1}
                                                        y={11}
                                                        width={9}
                                                        height={9}
                                                        fill="#00a4ef"
                                                    />
                                                    <rect
                                                        x={11}
                                                        y={1}
                                                        width={9}
                                                        height={9}
                                                        fill="#7fba00"
                                                    />
                                                    <rect
                                                        x={11}
                                                        y={11}
                                                        width={9}
                                                        height={9}
                                                        fill="#ffb900"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <span className="gsi-material-button-contents">
                                            {isMicrosoftLoading
                                                ? t("auth.modal.loading")
                                                : activeTab === "login"
                                                ? "Sign in with Microsoft"
                                                : "Sign up with Microsoft"}
                                        </span>
                                    </div>
                                </button>
                            </div>

                            {/* Divider */}
                            <div className="relative mb-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="bg-white px-2 text-gray-500">
                                        {t("auth.modal.orContinueWithEmail")}
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : null}
                    {/* Error Messages */}
                    {error && (
                        <Message error className="mb-4">
                            {error?.message ||
                                error?.error ||
                                t("auth.signup.somethingWentWrong")}
                        </Message>
                    )}

                    {/* Success Messages */}
                    {successMessage && (
                        <Message success className="mb-4">
                            {successMessage}
                        </Message>
                    )}

                    {/* Token Verification Step */}
                    {isTokenStep ? (
                        <div className="space-y-4">
                            {!successMessage ? (
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        {t("auth.modal.verifyEmail")}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {t("auth.modal.verificationCodeSent")}{" "}
                                        {email}
                                    </p>
                                </div>
                            ) : null}

                            <form
                                onSubmit={
                                    activeTab === "login"
                                        ? handleLoginTokenSubmit
                                        : handleSignupVerification
                                }
                                className="space-y-4 px-2 sm:px-0"
                            >
                                <Input
                                    id="email"
                                    name="email"
                                    label={t("auth.modal.email")}
                                    type="email"
                                    value={email}
                                    readOnly
                                />

                                <Input
                                    id="token"
                                    name="token"
                                    label={t("auth.modal.verificationCode")}
                                    type="text"
                                    placeholder={t(
                                        "auth.modal.verificationCodePlaceholder"
                                    )}
                                    autoComplete="off"
                                    required
                                    autoFocus
                                />

                                {loginError && (
                                    <Message error>
                                        {loginError?.message ||
                                            t(
                                                "auth.modal.invalidVerificationCode"
                                            )}
                                    </Message>
                                )}

                                <div className="text-center text-sm text-gray-600">
                                    {resendCountdown > 0 ? (
                                        <span>
                                            {t("auth.modal.resendCodeIn", {
                                                seconds: resendCountdown,
                                            }) ||
                                                `Request new code in ${resendCountdown}s`}
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleResendCode}
                                            disabled={isResending}
                                            className="text-indigo-600 hover:text-indigo-500 font-medium disabled:opacity-50"
                                        >
                                            {isResending
                                                ? t("auth.modal.sending") ||
                                                  "Sending..."
                                                : t("auth.modal.resendCode") ||
                                                  "Request new code"}
                                        </button>
                                    )}
                                </div>

                                <div className="flex gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setIsTokenStep(false);
                                            setSuccessMessage(null);
                                            setResendCountdown(0);
                                            setResendAttempts(0);
                                            sessionStorage.removeItem("auth_modal_otp_state");
                                        }}
                                        className="flex-1"
                                    >
                                        {t("auth.modal.back")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="flex-1"
                                        isLoading={isLoginLoading}
                                        disabled={isLoginLoading}
                                    >
                                        {t("auth.modal.verifyAndContinue")}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        /* Login/Signup Tabs */
                        <Tabs
                            value={activeTab}
                            onValueChange={setActiveTab}
                            className="w-full"
                        >
                            <TabsList className="grid w-full grid-cols-2 h-12">
                                <TabsTrigger
                                    value="login"
                                    className="text-sm sm:text-base"
                                >
                                    {t("auth.modal.signIn")}
                                </TabsTrigger>
                                <TabsTrigger
                                    value="signup"
                                    className="text-sm sm:text-base"
                                >
                                    {t("auth.modal.signUp")}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="login" className="mt-4">
                                <form
                                    onSubmit={handleLoginEmailSubmit}
                                    className="space-y-4 px-2 sm:px-0"
                                >
                                    <Input
                                        id="login-email"
                                        name="email"
                                        label={t("auth.modal.email")}
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        placeholder={t(
                                            "auth.modal.emailPlaceholder"
                                        )}
                                        autoComplete="email"
                                        required
                                        autoFocus
                                    />

                                    {ssoAvailable && (
                                        <Button
                                            type="button"
                                            className="w-full"
                                            variant="outline"
                                            onClick={handleSSOLogin}
                                            isLoading={isSSOLoading}
                                        >
                                            {t("auth.modal.loginWithSSO")}
                                        </Button>
                                    )}

                                    <Button
                                        type="submit"
                                        className="w-full"
                                        isLoading={requestCodeIsLoading}
                                        disabled={requestCodeIsLoading}
                                    >
                                        {t("auth.modal.sendVerificationCode")}
                                    </Button>
                                </form>
                            </TabsContent>

                            <TabsContent value="signup" className="mt-4">
                                <form
                                    onSubmit={handleSignupSubmit}
                                    className="space-y-4 px-2 sm:px-0"
                                >
                                    <Input
                                        id="signup-fullname"
                                        name="fullName"
                                        label={t("auth.modal.fullName")}
                                        type="text"
                                        value={fullName}
                                        onChange={(e) =>
                                            setFullName(e.target.value)
                                        }
                                        autoComplete="name"
                                        required
                                        placeholder={t(
                                            "auth.modal.fullNamePlaceholder"
                                        )}
                                    />

                                    <Input
                                        id="signup-email"
                                        name="email"
                                        label={t("auth.modal.email")}
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        autoComplete="email"
                                        required
                                        placeholder={t(
                                            "auth.modal.emailPlaceholder"
                                        )}
                                    />

                                    <div className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id="terms"
                                                name="terms"
                                                type="checkbox"
                                                checked={acceptedTerms}
                                                onChange={(e) =>
                                                    setAcceptedTerms(
                                                        e.target.checked
                                                    )
                                                }
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                required
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label
                                                htmlFor="terms"
                                                className="font-medium text-gray-700"
                                            >
                                                {t("auth.modal.termsAgreement")}{" "}
                                                <InternalLink
                                                    to="https://www.agentui.ai/terms"
                                                    className="font-semibold text-indigo-600 hover:text-indigo-500"
                                                >
                                                    {t(
                                                        "auth.modal.termsOfService"
                                                    )}
                                                </InternalLink>
                                            </label>
                                        </div>
                                    </div>

                                    {signupError && (
                                        <Message error>
                                            {signupError?.error ||
                                                t(
                                                    "auth.signup.somethingWentWrong"
                                                )}
                                        </Message>
                                    )}

                                    <Button
                                        type="submit"
                                        className="w-full"
                                        isLoading={isSignupLoading}
                                        disabled={isSignupLoading}
                                    >
                                        {t("auth.signup.createAccount")}
                                    </Button>
                                </form>
                            </TabsContent>
                        </Tabs>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default AuthModal;
